import dayjs from 'dayjs';
import { useMemo } from 'react';
import {
  GridColDef,
  GridCellParams,
  GridColTypeDef,
  GRID_DATE_COL_DEF,
  getGridDateOperators,
  GridRenderEditCellParams,
} from '@mui/x-data-grid-premium';
import { useIntl } from 'react-intl';
import { User } from 'library/types';
import { alpha, Chip } from '@mui/material';
import { renderFormattedDate } from 'utils';
import { Theme } from '@mui/material/styles';
import { GridEditDateCell, GridFilterDateInput, UserAutocompleteEditCell } from 'components';
import { PROJECT_STEP_PRIORITY_TYPE_ENUM, PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM } from 'enums';

type Params = {
  theme: Theme;
  users: User[];
  priorityOptions: { value: number; label: string }[];
  responsibleContactOptions: { value: number; label: string }[];
  getProjectStepPriorityTypeTranslated: (value: number) => string;
};

export const useCreateProjectPlanColumns = ({
  theme,
  users,
  priorityOptions,
  responsibleContactOptions,
  getProjectStepPriorityTypeTranslated,
}: Params): GridColDef[] => {
  const intl = useIntl();

  const dateColumnType: GridColTypeDef<Date, string> = useMemo(
    () => ({
      ...GRID_DATE_COL_DEF,
      resizable: false,
      renderEditCell: (params) => {
        const v = params.value;
        const dayjsValue = v instanceof Date ? dayjs(v) : v ? dayjs(v) : null;

        return <GridEditDateCell {...params} value={dayjsValue} />;
      },
      filterOperators: getGridDateOperators(false).map((item) => ({
        ...item,
        InputComponent: GridFilterDateInput,
        InputComponentProps: { showTime: false },
      })),
    }),
    []
  );

  return useMemo(
    () => [
      {
        field: 'orderNo',
        headerName: intl.formatMessage({
          id: 'projectPlanCreate.orderNo',
          defaultMessage: 'Sıra',
        }),
        width: 90,
        align: 'center',
        headerAlign: 'center',
        editable: false,
      },
      {
        field: 'title',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.projectStep',
          defaultMessage: 'Proje Adımı',
        }),
        minWidth: 220,
        flex: 1,
        editable: true,
      },
      {
        field: 'description',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.stepExplanation',
          defaultMessage: 'Adım Açıklaması',
        }),
        minWidth: 220,
        flex: 1,
        editable: true,
      },
      {
        field: 'priority',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.priority',
          defaultMessage: 'Öncelik',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        editable: true,
        valueOptions: priorityOptions.map((x) => x.value),
        getOptionLabel: (value) => priorityOptions.find((x) => x.value === value)?.label ?? String(value),
        valueFormatter: (value) => priorityOptions.find((x) => x.value === value)?.label ?? '',
        renderCell: (params) => {
          const priority = params.value ?? PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT;
          const colorKey =
            priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT
              ? 'success'
              : priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT
                ? 'warning'
                : 'error';

          return (
            <Chip
              size="small"
              label={getProjectStepPriorityTypeTranslated(priority)}
              sx={{
                fontWeight: 600,
                bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                color: theme.palette[colorKey].main,
                border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              }}
            />
          );
        },
        minWidth: 160,
      },
      {
        field: 'relatedContactId',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.relatedContact',
          defaultMessage: 'İlgili Kişisi',
        }),
        headerAlign: 'center',
        align: 'center',
        editable: true,
        valueFormatter: (value) => users.find((user) => user.id === value)?.nameSurname ?? '',
        renderEditCell: (params: GridRenderEditCellParams) => <UserAutocompleteEditCell {...params} users={users} />,
        minWidth: 200,
      },
      {
        field: 'alternativeRelatedContactId',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.alternativeRelatedContact',
          defaultMessage: 'Yedek İlgili Kişisi',
        }),
        headerAlign: 'center',
        align: 'center',
        editable: true,
        valueFormatter: (value) => users.find((user) => user.id === value)?.nameSurname ?? '',
        renderEditCell: (params: GridRenderEditCellParams) => <UserAutocompleteEditCell {...params} users={users} />,
        minWidth: 220,
      },
      {
        field: 'responsibleContactType',
        headerName: intl.formatMessage({
          id: 'productProjectPlan.responsibleContact',
          defaultMessage: 'Sorumlu',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        editable: true,
        valueOptions: responsibleContactOptions.map((x) => x.value),
        getOptionLabel: (value) => responsibleContactOptions.find((x) => x.value === value)?.label ?? String(value),
        valueFormatter: (value) => responsibleContactOptions.find((x) => x.value === value)?.label ?? '',
        minWidth: 180,
      },
      {
        field: 'responsibleCustomerFullName',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.responsibleCustomerFullName',
          defaultMessage: 'Müşteri Sorumlu Ad Soyad',
        }),
        editable: true,
        isCellEditable: (params: GridCellParams) =>
          params.row.responsibleContactType !== PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON,
        minWidth: 220,
      },
      {
        field: 'estimatedTaskDay',
        headerName: intl.formatMessage({
          id: 'productProjectPlan.estimatedTaskDay',
          defaultMessage: 'Tahmini Adım Süresi (gün)',
        }),
        headerAlign: 'center',
        align: 'center',
        editable: true,
        type: 'number',
        minWidth: 180,
      },
      // {
      //   field: 'additionalEffortDay',
      //   headerName: intl.formatMessage({
      //     id: 'projectStepManaging.additionalEffortDay',
      //     defaultMessage: 'Ek Efor Süresi (gün)',
      //   }),
      //   headerAlign: 'center',
      //   align: 'center',
      //   editable: true,
      //   type: 'number',
      //   minWidth: 180,
      // },
      {
        ...dateColumnType,
        field: 'dueDate',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.terminateDate',
          defaultMessage: 'Termin',
        }),
        headerAlign: 'center',
        align: 'center',
        editable: false,
        minWidth: 170,
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value) : ''),
      },
      {
        field: 'notes',
        headerName: intl.formatMessage({
          id: 'projectStepManaging.notes',
          defaultMessage: 'Notlar',
        }),
        editable: true,
        minWidth: 220,
        flex: 1,
      },
    ],
    [
      intl,
      theme,
      users,
      dateColumnType,
      priorityOptions,
      responsibleContactOptions,
      getProjectStepPriorityTypeTranslated,
    ]
  );
};
